/**
 * Data Model
 *
 * Three top-level collections:
 * - projects     — client engagements Alek delivered
 * - apps         — products Alek built and distributes himself
 * - capabilities — the kinds of work he does, each referencing projects
 *
 * Static data structure designed for a React static site.
 */

// Client engagements
export const projects = [
  // Data & Analytics Engineering Projects
  {
    id: "crain-communications",
    client_name: "Crain Communications",
    title: "Warehouse Rebuild & Team Enablement",
    headline_outcome: "Took one brand's data stack and rolled it out across the portfolio",
    logo: "/project-logos/crain-communications.png",
    related: {
      project_id: "hart-energy",
      label: "Where this started",
      note: "I built Hart Energy's data stack before Crain acquired them. Crain kept me on to bring that work to the rest of their brands."
    },
    problem_statement: "Crain Communications had a nascent data warehouse and legacy systems resulted in unreliable data delivery, low confidence in insights, and lack of breadth in analytic capabilities. The team wasn't trained on modern data stack best practices.",
    solution: "Implemented modern data warehouse architecture in Lambda, Glue, Redshift, and DBT. Rebuilt ETL pipelines from scratch, improving the reliability and creating thorough documentation. Trained team on best practices.",
    details: [
      "Replaced every legacy ETL pipeline with AWS and DBT, ending the unreliable delivery the team had been working around",
      "Put governance and validation in front of the warehouse, so the numbers could be trusted enough to act on",
      "Built a semantic layer across the business data so teams could get answers through Crain's own enterprise AI tooling",
      "Built an evaluation system that verified the LLM was using the right data before anyone relied on the answer",
      "Brought every key source into the warehouse, closing the gaps that had limited what could be analysed",
      "Left runbooks and training detailed enough that the team kept running the stack after I left",
      "Carried the architecture built for Hart Energy across Crain's other brands after the acquisition"
    ]
  },
  {
    id: "hart-energy",
    client_name: "Hart Energy",
    title: "A Data Stack From Zero",
    headline_outcome: "Built the reporting suite the whole company ran on — then Crain acquired them and kept me",
    logo: "/project-logos/hart-energy.png",
    related: {
      project_id: "crain-communications",
      label: "What happened next",
      note: "Crain Communications acquired Hart Energy. They retained me through the acquisition and had me take the stack I had built here and apply it across their brands."
    },
    problem_statement: "Hart Energy had no existing data infrastructure and relied exclusively on data connections between external platforms. They lacked revenue reporting, flexible analyses, and all analyses were highly manual because they relied on manual exports and joins between platforms like Cvent (conferences) and Stripe (subscriptions).",
    solution: "Built an affordable AWS data stack without relying on budget increases or new platforms. Created end-to-end data pipeline for key data assets into a centralized warehouse. Built the reporting suite used by the entire company to evaluate their subscription and conference businesses. Helped the team shift to an OKR-driven culture.",
    details: [
      "Stood up an AWS data lake on the existing budget, with no new platforms to buy",
      "Wired Cvent, Stripe and Google Analytics together, ending the manual exports and hand-joins every analysis depended on",
      "Built the reporting suite the whole company used to run its subscription and conference businesses",
      "Moved the team onto OKRs — their VP of Digital credits the shift with revenue reaching an all-time high"
    ]
  },
  {
    id: "mastercard",
    client_name: "Mastercard",
    title: "Business Experimentation",
    headline_outcome: "Delivered $200K+ of analytics projects to enterprise clients",
    logo: "/project-logos/mastercard.png",
    problem_statement: "With an excess of Q4 projects, the Mastercard team needed support to deliver high-quality analyses to their clients with minimal supervision or overhead.",
    solution_heading: "What I Did",
    solution: "Embedded with the team and ran engagements end to end — scoping with account managers, mentoring junior analysts through the analysis, and shipping the work on time and inside budget.",
    details: [
      "Scoped engagements directly with account managers and sales, so work started without partner oversight",
      "Added delivery capacity by running junior analysts through the work rather than around them",
      "Closed $200K+ of client analytics on time and inside budget"
    ]
  },
  {
    id: "amplify-data",
    client_name: "Amplify Data",
    title: "Multi-Client Data Platform",
    headline_outcome: "Foundation that won their first clients and led to acquisition",
    logo: "/project-logos/amplify-data.png",
    problem_statement: "Amplify Data needed to build a scalable platform to validate and visualize data.",
    solution: "Partnered with the CTO to develop a robust data validation API that would scale across their future clients. Ultimately, this foundational work would help Amplify acquire its first clients and eventually get acquired by Monda.",
    details: [
      "Architected a multi-tenant warehouse that scaled to new clients instead of being rebuilt for each one",
      "Built the validation layer their enterprise clients' data had to clear before it reached the product",
      "Automated quality monitoring so problems surfaced internally rather than in front of customers",
      "Shipped the data visualisations that became a customer-facing surface of the product"
    ]
  },

  // Applied AI & Machine Learning Projects
  {
    id: "insurity",
    client_name: "Insurity",
    title: "Neural Network Modeling",
    headline_outcome: "100% more efficient than industry-standard flood models",
    logo: "/project-logos/insurity.png",
    problem_statement: "Legacy models for property-level flood risk prediction were inaccurate and couldn't capture complex patterns in the data or anomalous behavior.",
    solution: "Developed an industry-grade neural network based flood risk model by aggregating both public and private data sources.",
    details: [
      "Aggregated weather, topography, structural and hydrography data into a single modelling set",
      "Built a feature-creation tool so new model features could be generated without rebuilding the pipeline",
      "Had a working neural-network prototype in front of them within weeks, then improved it feature by feature",
      "100% more efficient than industry-standard models at predicting floods where none had previously occurred",
      "Left a defined path to extend the same approach to wildfire and earthquake"
    ]
  },
  {
    id: "good-cents",
    client_name: "Good Cents",
    title: "Personal Finance AI Advisor",
    headline_outcome: "Took a text-message AI advisor from nothing to launched",
    icon: "💰",
    logo: "/project-logos/good-cents.png",
    problem_statement: "Good Cents needed a technical partner to help them create a text message based AI financial advisor that would provide accessible and personalized financial guidance.",
    solution: "Designed the core data architecture and the agentic framework behind the advisor, then drove it to launch — bringing in a developer, directing the build, and holding it to the founder's budget and standards.",
    details: [
      "Built the data architecture the advisor runs on",
      "Implemented an agentic framework so the advisor could handle multi-step financial questions, not just lookups",
      "Delivered it over SMS, so users needed no app and no account",
      "Took it to a launched first version inside the founder's budget and standards"
    ]
  },

  // Productization & Prototyping Projects
  {
    id: "game-boost-analytics",
    client_name: "Game Boost Analytics",
    // Kept in full on /work; only excluded from the hero logo strip, where the flat
    // wordmark reads as much lower fidelity than the marks beside it.
    hide_from_logo_strip: true,
    title: "AI Video Game Coach",
    headline_outcome: "Computer vision that shows players their own mistakes",
    logo: "/project-logos/game-boost-analytics.png",
    problem_statement: "Gamers lacked effective systems to improve their gameplay. Personalized coaching was expensive and time-consuming, and existing analytics tools failed to provide actionable insights due to low data availability.",
    solution: "Developed an AI video game coach which would watch you play, analyze your gameplay, and give you insights for how you could improve.",
    details: [
      "Built video ingestion processes through Twitch APIs",
      "Developed computer vision models for gameplay analysis",
      "Created intuitive dashboard for players to analyze their performance",
      "Implemented data-driven video generation to highlight key moments where players did well or could improve",
    ]
  },
  {
    id: "augment-analytics",
    client_name: "Augment Analytics",
    title: "Talent Matching AI",
    headline_outcome: "Matching MVP defined and shipped in a few weeks",
    logo: "/project-logos/augment-analytics.png",
    problem_statement: "Augment Analytics needed AI to help more effectively and efficiently match Augment's large talent bench with client needs.",
    solution: "Developed an AI-driven talent matching platform to connect clients with the right members of the Augment team.",
    details: [
      "Matched against the resume database Augment already had, with nothing to migrate",
      "Gave the team a single place to submit a client request and get candidates back",
      "Validated matches against live projects before anyone relied on them",
      "Scoped and shipped the whole MVP in a few weeks, at a cost the client set"
    ]
  }
];

/**
 * Products Alek built and distributes.
 *
 * These are deliberately NOT in `projects` — they are things a visitor can go use,
 * not case studies. Both are paid desktop apps bought once and owned, so the primary
 * CTA points at each product's own homepage (where the pitch and the purchase live)
 * rather than deep-linking a download the visitor cannot use without buying first.
 */
export const apps = [
  {
    id: "solidly-finance",
    name: "Solidly Finance",
    tagline: "Chart your path to financial goals.",
    logo: "/project-logos/solidly-finance.png",
    description: "A private, offline financial planner you own outright. Everything is encrypted on your own device — no servers, no syncing, no account to create.",
    highlights: [
      "Works offline; your financial data never leaves your computer",
      "What-if scenarios, debt payoff strategies, and goal tracking",
      "Buy once, own it for life — no subscription"
    ],
    platforms: ["Windows", "macOS"],
    requirements: "No account, no connection, no API keys. Install it and it works.",
    cta: {
      label: "Get Solidly Finance",
      url: "https://finance.solidly.ai"
    },
    secondaryCta: {
      label: "Watch the demo",
      url: "https://finance.solidly.ai/demo"
    }
  },
  {
    id: "solidly-meet",
    name: "Solidly Meet",
    tagline: "Bring Claude into your meetings. Live.",
    logo: "/project-logos/solidly-logo.png",
    description: "A desktop app that puts Claude in the room with you. No bot joins the call — it runs on your machine, follows the conversation, and puts the answer on the table before you think to ask.",
    highlights: [
      "No bot joins your calls; transcripts stay in a local database",
      "Live support cards fire mid-conversation from skills you teach it",
      "Speaker ID, shaped summaries, and notes delivered via your own MCP servers"
    ],
    platforms: ["Windows", "macOS soon"],
    requirements: "Runs on your own Claude plan (Pro or Max) plus an AssemblyAI key for transcription — you pay those providers directly, not me.",
    cta: {
      label: "Get Solidly Meet",
      url: "https://meet.solidly.ai"
    },
    secondaryCta: {
      label: "Watch the demo",
      url: "https://meet.solidly.ai/demo"
    }
  }
];

/**
 * Testimonials.
 *
 * `body` is either a single string or an array of paragraphs — use the array form for
 * anything long enough to need breaks. `excerpt` is optional and only used by the
 * homepage carousel, which has a fixed-height card; without it the carousel would
 * resize on every rotation. An excerpt must be a verbatim, contiguous run of the
 * body, never a stitched-together paraphrase.
 */
export const testimonials = [
  {
    id: "crain-communications-testimonial-1",
    project_id: "crain-communications",
    name: "Sean Bales",
    title: "Senior Director, Product & Data",
    picture: "/testimonial-images/sean-bales.jpg",
    body: [
      "Alek was our data engineer at Crain for the last couple of years, and by the end he was the go-to person for anything touching our data warehouse. He built and owned the pipelines feeding it, the models on top of it, and the alerting around it.",
      "Once that foundation was in place, he helped take our data capabilities to the next level. He built a semantic layer across our business data that allowed teams to access insights through our enterprise AI tooling. His attention to detail, quality, and accuracy really stood out, including building an evaluation system to make sure the LLM was using the right data and returning reliable answers to business-critical questions.",
      "One of the things that impressed me most was how he left. The runbooks and documentation he created for recurring processes, integrations, and everything he owned were meticulous. He made sure the team was set up to succeed when he was gone.",
      "I'd work with Alek again without thinking twice."
    ],
    excerpt: "One of the things that impressed me most was how he left. The runbooks and documentation he created for recurring processes, integrations, and everything he owned were meticulous. He made sure the team was set up to succeed when he was gone. I'd work with Alek again without thinking twice."
  },
  {
    id: "hart-energy-testimonial-1",
    project_id: "hart-energy",
    name: "Stacy Eisner",
    title: "Chief Digital Officer",
    picture: "/testimonial-images/stacy.jpg",
    body: "Alek joined Hart Energy as a BI consultant and quickly took on a more impactful role. With a tactful and collaborative approach, Alek became a trusted advisor to each of my team members. He identified areas of opportunity and documented, trained, and partnered with the team to lead by example and upskill their performance. Alek has been an indispensable asset to my organization through a challenging cultural transition and his influence will translate into business results for the future."
  },
  {
    id: "hart-energy-testimonial-2",
    project_id: "hart-energy",
    name: "Derick Baker",
    title: "VP of Digital",
    picture: "/testimonial-images/derick.jpg",
    body: "Alek is an indispensable asset for the Hart Energy Digital team. His impact goes beyond building dashboards and sharing insights. He's enabled us to become a data-driven company and, fueled by his insights, our revenue has soared higher than it has ever been. He is highly organized, a great communicator, and capable of managing the kinds of large-scale projects that push our business forward. Hiring Alek is one of the most intelligent business decisions I could have made."
  },
  {
    id: "amplify-data-testimonial-1",
    project_id: "amplify-data",
    name: "Ameya Pathare",
    title: "Chief Technology Officer",
    picture: "/testimonial-images/ameya.jpg",
    body: "Alek built a reliable infrastructure to handle key data visualizations for our product. He partnered with us throughout the development process to quickly understand our needs and build a solution to support our enterprise clients."
  },
  {
    id: "good-cents-testimonial-1",
    project_id: "good-cents",
    name: "Stealth Founder",
    title: "Founder & CEO",
    picture: "/testimonial-images/placeholder.jpg",
    body: "Good Cents engaged Alek to build the first launchable version of our product. I wanted Good Cents built efficiently and according to best practices. Alek made that happen. He leveraged his connections to find a capable developer. After we aligned on a scope of work, he worked closely with the developer to ensure the product met my budget, requirements, and standards. I enjoyed working with Alek, and I fully trust him and the quality of his work."
  },
  {
    id: "augment-analytics-testimonial-1",
    project_id: "augment-analytics",
    name: "Brett Ludden",
    title: "Co-founder & Managing Partner",
    picture: "/testimonial-images/brett.jpg",
    body: "Alek partnered with Augment to help us understand whether AI could streamline some of our project intake processes. He collaborated with me to define a cost-affordable MVP we executed in only a few weeks."
  },
  {
    id: "insurity-testimonial-1",
    project_id: "insurity",
    name: "Jatin Atre, Ph.D.",
    title: "Chief Business Officer",
    picture: "/testimonial-images/jatin.jpg",
    body: "We wanted to learn if neural networks would be an effective way to predict unprecedented property-level flood risk. Alek did an amazing job of quickly developing the neural network model. Within weeks, we had a functioning prototype. As he progressively layered in additional model features, the performance continued to improve, eventually surpassing many of the industry-standard predictive models."
  },
];

// The kinds of work Alek does, each referencing the projects that demonstrate it.
// Rendered with `icon` rather than photography: the stock imagery these cards used
// (a robot hand reaching for a human one, a glowing keyboard) read as filler next to
// the specific, checkable claims everywhere else on the page.
export const capabilities = [
  {
    id: "data-analytics-engineering",
    title: "Data & Analytics Engineering",
    icon: "📊",
    description: "Warehouses, pipelines, and reporting that a team can actually trust and extend.",
    features: [
      "Modern data warehouse architecture (Redshift, BigQuery, Snowflake, DBT, Fivetran)",
      "Data validation, governance, and visualization",
      "Incremental and real-time data pipelines",
      "Enterprise data lake and warehouse design"
    ],
    project_ids: [
      "crain-communications",
      "mastercard",
      "hart-energy",
      "amplify-data"
    ]
  },
  {
    id: "applied-ai-ml",
    title: "Applied AI & Machine Learning",
    icon: "🤖",
    description: "Models that hold up in production and move a real number, not just demo well.",
    features: [
      "Predictive modeling and neural networks for business outcomes",
      "AI copilots and agents for internal productivity",
      "Natural language understanding, summarization, and automation",
      "Computer vision and document processing"
    ],
    project_ids: [
      "augment-analytics",
      "good-cents",
      "insurity"
    ]
  },
  {
    id: "productization-prototyping",
    title: "Productization & Prototyping",
    icon: "🚀",
    description: "Turning a working model into something people can install, open, and use.",
    features: [
      "MVP AI app design and launch",
      "Human-AI interface design and UX",
      "Private and on-premise AI solutions for enterprise",
      "Full-stack development and deployment"
    ],
    project_ids: [
      "game-boost-analytics",
      "augment-analytics",
      "good-cents"
    ]
  }
];

// Helper functions for data access
export const getCapabilityById = (capabilityId) => {
  return capabilities.find(capability => capability.id === capabilityId);
};

export const getProjectById = (projectId) => {
  return projects.find(project => project.id === projectId);
};

export const getAppById = (appId) => {
  return apps.find(app => app.id === appId);
};

export const getProjectsForCapability = (capabilityId) => {
  const capability = getCapabilityById(capabilityId);
  if (!capability) return [];

  return capability.project_ids.map(projectId => getProjectById(projectId)).filter(Boolean);
};

export const getCapabilityForProject = (projectId) => {
  return capabilities.find(capability => capability.project_ids.includes(projectId));
};

export const getTestimonialsForProject = (projectId) => {
  return testimonials.filter(testimonial => testimonial.project_id === projectId);
};

// Normalise `body` to an array so render sites can map over paragraphs uniformly,
// whether the testimonial was written as one string or several.
export const getTestimonialParagraphs = (testimonial) => {
  return Array.isArray(testimonial.body) ? testimonial.body : [testimonial.body];
};

// Short form for the fixed-height homepage carousel. Falls back to the full body
// when no excerpt is defined, which is the case for the shorter testimonials.
export const getTestimonialExcerpt = (testimonial) => {
  return testimonial.excerpt || getTestimonialParagraphs(testimonial).join(' ');
};

// Projects grouped by capability, in capability order — used by the work index
export const getProjectsGroupedByCapability = () => {
  return capabilities.map(capability => ({
    capability,
    projects: getProjectsForCapability(capability.id)
  }));
};

// Projects in the order /work presents them, de-duplicated. A project can be listed
// under more than one capability, so keep only its first appearance.
export const getOrderedProjects = () => {
  const seen = new Set();
  return getProjectsGroupedByCapability()
    .flatMap(group => group.projects)
    .filter(project => {
      if (seen.has(project.id)) return false;
      seen.add(project.id);
      return true;
    });
};

// The project a case study is narratively connected to — Hart Energy and Crain are
// one continuous engagement across an acquisition, not two unrelated clients.
export const getRelatedProject = (projectId) => {
  const project = getProjectById(projectId);
  if (!project || !project.related) return null;
  const target = getProjectById(project.related.project_id);
  if (!target) return null;
  return { ...project.related, project: target };
};

// Clients whose logo appears in the hero strip. A project can be excluded on logo
// quality alone without being demoted anywhere else on the site.
export const getLogoStripClients = () => {
  return getOrderedProjects().filter(project => !project.hide_from_logo_strip);
};

// Neighbouring projects for detail-page navigation. Wraps at both ends so no case
// study is a dead end.
export const getAdjacentProjects = (projectId) => {
  const ordered = getOrderedProjects();
  const index = ordered.findIndex(project => project.id === projectId);

  if (index === -1) return { previous: null, next: null };

  return {
    previous: ordered[(index - 1 + ordered.length) % ordered.length],
    next: ordered[(index + 1) % ordered.length]
  };
};

// Fallback shown when a client logo fails to load. Only one project defines an `icon`,
// so the previous fallback rendered an empty box for the other seven.
export const getClientInitials = (clientName) => {
  return clientName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word[0])
    .join('')
    .toUpperCase();
};

// A short list for the homepage "selected work" section
export const getFeaturedProjects = (limit = 6) => {
  const seen = new Set();
  const featured = [];

  for (const { projects: capabilityProjects } of getProjectsGroupedByCapability()) {
    for (const project of capabilityProjects) {
      if (!seen.has(project.id)) {
        seen.add(project.id);
        featured.push(project);
      }
    }
  }

  return featured.slice(0, limit);
};
